import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

interface AdminActivity {
  id: string;
  name: string;
  category: string;
  valueTier: string;
  isActive: boolean;
  sortOrder: number;
}

interface ActivityForm {
  name: string;
  category: string;
  valueTier: string;
  isActive: boolean;
}

const EMPTY_FORM: ActivityForm = {
  name: '',
  category: '',
  valueTier: 'B',
  isActive: true,
};

// The 8 marketplace tile keys — activities in these groups appear under a tile
const TILE_KEYS = [
  'robotics',
  'portfolio',
  'entrepreneurship',
  'research',
  'social impact',
  'business competition',
  'olympiad',
  'gifted',
];

const AdminActivities: React.FC = () => {
  const [activities, setActivities] = useState<AdminActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<ActivityForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getAdminActivities();
    if (!result.success) {
      setDenied(true);
      setLoading(false);
      return;
    }
    setActivities(result.activities || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const openEditor = (activity?: AdminActivity, presetCategory?: string) => {
    setError('');
    if (activity) {
      setEditing(activity.id);
      setForm({
        name: activity.name,
        category: activity.category,
        valueTier: activity.valueTier,
        isActive: activity.isActive,
      });
    } else {
      setEditing('new');
      setForm({ ...EMPTY_FORM, category: presetCategory || '' });
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const saveActivity = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      category: form.category.trim().toLowerCase(),
      valueTier: form.valueTier,
      isActive: form.isActive,
    };
    const result =
      editing === 'new'
        ? await apiService.createActivity(payload)
        : await apiService.updateActivity(editing as string, {
            ...payload,
            sortOrder: activities.find((a) => a.id === editing)?.sortOrder ?? 0,
          });
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not save activity.');
      return;
    }
    closeEditor();
    fetchActivities();
  };

  const deleteActivity = async (activity: AdminActivity) => {
    if (
      !window.confirm(
        `Delete "${activity.name}"? Existing mentor achievements keep their category, but this option disappears from signup and marketplace chips.`
      )
    )
      return;
    const result = await apiService.deleteActivity(activity.id);
    if (!result.success) {
      window.alert(result.error || 'Could not delete activity.');
      return;
    }
    setActivities((prev) => prev.filter((a) => a.id !== activity.id));
  };

  const toggleActive = async (activity: AdminActivity) => {
    const result = await apiService.updateActivity(activity.id, {
      name: activity.name,
      category: activity.category,
      valueTier: activity.valueTier,
      sortOrder: activity.sortOrder,
      isActive: !activity.isActive,
    });
    if (!result.success) {
      window.alert(result.error || 'Could not update activity.');
      return;
    }
    setActivities((prev) =>
      prev.map((a) => (a.id === activity.id ? { ...a, isActive: !activity.isActive } : a))
    );
  };

  if (denied) {
    return (
      <div className="section">
        <div className="wrap" style={{ maxWidth: 760 }}>
          <div className="card" style={{ maxWidth: 520 }}>
            <h3 style={{ marginBottom: 8 }}>Admin access required</h3>
            <p className="meta">Sign in with an admin account to manage activities.</p>
          </div>
        </div>
      </div>
    );
  }

  // Group by category (preserves category/sortOrder ordering from API)
  const grouped: Array<{ category: string; items: AdminActivity[] }> = [];
  for (const activity of activities) {
    let cat = grouped.find((g) => g.category === activity.category);
    if (!cat) {
      cat = { category: activity.category, items: [] };
      grouped.push(cat);
    }
    cat.items.push(activity);
  }

  const categoryOptions = Array.from(
    new Set([...TILE_KEYS, ...activities.map((a) => a.category)])
  );

  const editorForm = (
    <div className="card" style={{ marginBottom: 20, borderColor: 'var(--cardinal)' }}>
      <h3 style={{ marginBottom: 12 }}>{editing === 'new' ? 'New activity' : 'Edit activity'}</h3>
      <div className="field" style={{ marginBottom: 12 }}>
        <label htmlFor="act-name">Activity name (shown to mentors and families)</label>
        <input
          id="act-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={120}
          placeholder="e.g., ISEF finalist/winner"
        />
      </div>
      <div className="row3" style={{ marginBottom: 12 }}>
        <div className="field">
          <label htmlFor="act-category">Category (marketplace tile group)</label>
          <input
            id="act-category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            maxLength={80}
            placeholder="e.g., research"
            list="act-categories"
          />
          <datalist id="act-categories">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div className="field">
          <label htmlFor="act-tier">Internal value tier — not shown to families</label>
          <select
            id="act-tier"
            value={form.valueTier}
            onChange={(e) => setForm({ ...form, valueTier: e.target.value })}
          >
            <option value="A">A</option>
            <option value="B">B</option>
          </select>
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          style={{ width: 'auto' }}
        />
        <span className="meta">Visible in signup and marketplace</span>
      </label>
      {error && (
        <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 12 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={saveActivity} disabled={saving}>
          {saving ? 'Saving…' : 'Save activity'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={closeEditor} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="section">
      <div className="wrap" style={{ maxWidth: 960 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Internal · admin
        </span>
        <h1 style={{ fontSize: 30, marginBottom: 8 }}>Activities</h1>
        <p className="meta" style={{ marginBottom: 24 }}>
          {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'} · mentors pick one of
          these at signup, and each active activity appears as a filter chip on the marketplace.
          Hidden activities stay here but aren't shown to mentors or families.
        </p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button className="btn btn-primary btn-sm" onClick={() => openEditor()}>
            + New activity
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <Link className="btn btn-secondary btn-sm" to="/admin/templates">
              Templates
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/users">
              Users
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/ops">
              Verification queue
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/feedback">
              User feedback
            </Link>
          </div>
        </div>

        {editing !== null && editorForm}

        {loading ? (
          <div className="card">
            <p className="meta">Loading activities…</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="card">
            <p className="meta">No activities yet. Create one to get started.</p>
          </div>
        ) : (
          grouped.map((cat) => (
            <div key={cat.category} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, margin: 0, textTransform: 'capitalize' }}>
                  {cat.category}
                </h2>
                {!TILE_KEYS.includes(cat.category) && (
                  <span className="meta">no marketplace tile — chips show in "all" view only</span>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => openEditor(undefined, cat.category)}
                >
                  + Add to this group
                </button>
              </div>
              <div className="card">
                {cat.items.map((activity) => (
                  <div className="list-row" key={activity.id}>
                    <div style={{ minWidth: 0 }}>
                      <b>{activity.name}</b>{' '}
                      <span className="meta">· tier {activity.valueTier}</span>
                      {!activity.isActive && (
                        <span
                          className="tag"
                          style={{
                            marginLeft: 8,
                            color: 'var(--cardinal)',
                            borderColor: 'var(--cardinal)',
                          }}
                        >
                          Hidden
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditor(activity)}
                      >
                        Edit
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(activity)}>
                        {activity.isActive ? 'Hide' : 'Show'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => deleteActivity(activity)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminActivities;
